/**
 * F34 — Preferenze dashboard per-utente per-organization.
 *
 * Persiste ordine card hero, card nascoste, tab nascosti.
 * Tabella DB: user_dashboard_preferences (PK user_id+organization_id).
 */
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';

/** Identificatori card hero (devono matchare quelli in EnhancedDashboardOverview). */
export type HeroCardId = 'won' | 'pipeline' | 'conversion' | 'avg_close' | 'avg_value' | 'lost' | 'margin';

/** Identificatori tab dashboard. */
export type DashboardTabId = 'overview' | 'sales' | 'materials' | 'operational';

export interface DashboardLayout {
  /** Ordine corrente delle card hero. Le card non in lista vengono appese in fondo nell'ordine di default. */
  hero_cards_order: HeroCardId[];
  /** Card hero nascoste dall'utente. */
  hero_cards_hidden: HeroCardId[];
  /** Tab nascosti dall'utente. */
  tabs_hidden: DashboardTabId[];
}

export const DEFAULT_LAYOUT: DashboardLayout = {
  hero_cards_order: ['won', 'pipeline', 'margin', 'conversion', 'avg_close', 'avg_value', 'lost'],
  hero_cards_hidden: [],
  tabs_hidden: [],
};

/** Normalizza il layout assicurando che tutte le card hero compaiano (anche se non in `hero_cards_order`). */
function normalizeLayout(raw: Partial<DashboardLayout> | null | undefined): DashboardLayout {
  const order = raw?.hero_cards_order ?? [];
  const hidden = raw?.hero_cards_hidden ?? [];
  const tabsHidden = raw?.tabs_hidden ?? [];

  // Appendi le card di default che mancano (es. utente con layout vecchio + nuova card aggiunta in seguito).
  const allCards = DEFAULT_LAYOUT.hero_cards_order;
  const missing = allCards.filter(c => !order.includes(c));
  return {
    hero_cards_order: [...order.filter(c => allCards.includes(c)), ...missing],
    hero_cards_hidden: hidden.filter(c => allCards.includes(c)),
    tabs_hidden: tabsHidden,
  };
}

export const useDashboardPreferences = () => {
  const { user } = useAuth();
  const { currentOrganizationId } = useCurrentOrganization();
  const queryClient = useQueryClient();

  const orgId = currentOrganizationId;

  const query = useQuery({
    queryKey: ['dashboard-preferences', user?.id, orgId],
    queryFn: async (): Promise<DashboardLayout> => {
      if (!user || !orgId) return DEFAULT_LAYOUT;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('user_dashboard_preferences')
        .select('layout')
        .eq('user_id', user.id)
        .eq('organization_id', orgId)
        .maybeSingle();

      if (error) {
        console.error('[F34] failed to load dashboard preferences:', error);
        return DEFAULT_LAYOUT;
      }
      return normalizeLayout(data?.layout);
    },
    enabled: !!user && !!orgId,
    staleTime: 60_000,
  });

  const updateMutation = useMutation({
    mutationFn: async (next: DashboardLayout) => {
      if (!user || !orgId) throw new Error('User or organization not loaded');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('user_dashboard_preferences')
        .upsert({
          user_id: user.id,
          organization_id: orgId,
          layout: next,
        }, { onConflict: 'user_id,organization_id' });
      if (error) throw error;
      return next;
    },
    onSuccess: (next) => {
      queryClient.setQueryData(['dashboard-preferences', user?.id, orgId], next);
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      if (!user || !orgId) throw new Error('User or organization not loaded');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('user_dashboard_preferences')
        .delete()
        .eq('user_id', user.id)
        .eq('organization_id', orgId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.setQueryData(['dashboard-preferences', user?.id, orgId], DEFAULT_LAYOUT);
    },
  });

  return useMemo(() => ({
    layout: query.data ?? DEFAULT_LAYOUT,
    isLoading: query.isLoading,
    saveLayout: updateMutation.mutateAsync,
    isSaving: updateMutation.isPending,
    resetLayout: resetMutation.mutateAsync,
    isResetting: resetMutation.isPending,
  }), [query.data, query.isLoading, updateMutation.mutateAsync, updateMutation.isPending, resetMutation.mutateAsync, resetMutation.isPending]);
};
